import { supabase, isSupabaseConfigured } from './supabaseClient';

export function rowToArticle(r) {
  if (!r) return null;
  return {
    id: r.slug,
    title: r.title,
    title_vn: r.title_vn ?? '',
    date: r.published_on,
    updatedAt: r.updated_at ?? null,
    practiceArea: r.pa_slug,
    author: r.author_text ?? '',
    excerpt: r.excerpt ?? '',
    excerpt_vn: r.excerpt_vn ?? '',
    content: r.content_html ?? '',
    content_html_vn: r.content_html_vn ?? '',
    metaDescription: r.meta_description ?? '',
    seoTitle: r.seo_title ?? '',
    primaryKeyword: r.primary_keyword ?? '',
    secondaryKeywords: r.secondary_keywords ?? '',
    has_pdf: Boolean(r.has_pdf),
    pdf_note: r.pdf_note ?? '',
    pdf_base64: r.pdf_base64 ?? null,
  };
}

export function buildPaMapFromLabelRows(rows, lang) {
  const o = {};
  if (!rows?.length) return o;
  const vi = lang?.startsWith('vi');
  for (const row of rows) {
    if (!row.slug) continue;
    const label = vi && row.label_vn?.trim() ? row.label_vn : row.label;
    if (label) o[row.slug] = label;
  }
  return o;
}

export async function fetchPaLabels() {
  if (!isSupabaseConfigured() || !supabase) {
    return { labels: null, error: 'not_configured', paMap: {} };
  }
  const { data, error } = await supabase
    .from('pa_labels')
    .select('slug,label,label_vn,sort_order')
    .order('sort_order', { ascending: true });
  if (error) {
    console.warn('[pa_labels]', error.message);
    return { labels: null, error: error.message, paMap: {} };
  }
  const labels = data ?? [];
  return { labels, error: null, paMap: buildPaMapFromLabelRows(labels) };
}

export async function fetchPublishedArticlesList() {
  if (!isSupabaseConfigured() || !supabase) {
    return { articles: [], paMap: {}, source: 'none', labels: null, listError: null };
  }

  const [{ labels, paMap, error: paErr }, artRes] = await Promise.all([
    fetchPaLabels(),
    supabase
      .from('articles')
      .select(
        'slug,title,title_vn,published_on,pa_slug,author_text,excerpt,excerpt_vn,has_pdf,pdf_note',
      )
      .eq('published', true)
      .order('published_on', { ascending: false }),
  ]);

  const effectivePaMap = paMap || {};

  if (artRes.error) {
    console.warn('[articles]', artRes.error.message);
    return {
      articles: [],
      paMap: effectivePaMap,
      source: 'supabase',
      labels,
      listError: artRes.error.message,
      paLabelsError: paErr,
    };
  }

  const rows = artRes.data ?? [];
  return {
    articles: rows.map((r) => rowToArticle(r)),
    paMap: effectivePaMap,
    labels,
    source: 'supabase',
    listError: null,
    paLabelsError: paErr,
  };
}

/** Home preview — same ordering as Legal Updates list. */
export async function fetchRecentPublishedArticles(limit = 3) {
  if (!isSupabaseConfigured() || !supabase) {
    return { articles: [], source: 'none', listError: null };
  }

  const artRes = await supabase
    .from('articles')
    .select(
      'slug,title,title_vn,published_on,pa_slug,author_text,excerpt,excerpt_vn,has_pdf,pdf_note',
    )
    .eq('published', true)
    .order('published_on', { ascending: false })
    .limit(limit);

  if (artRes.error) {
    console.warn('[articles]', artRes.error.message);
    return { articles: [], source: 'supabase', listError: artRes.error.message };
  }

  return {
    articles: (artRes.data ?? []).map((r) => rowToArticle(r)),
    source: 'supabase',
    listError: null,
  };
}

export async function fetchArticlePublic(slug) {
  if (!isSupabaseConfigured() || !supabase) {
    return { article: null, source: 'none' };
  }

  const { data, error } = await supabase
    .from('articles')
    .select('*')
    .eq('slug', slug)
    .eq('published', true)
    .maybeSingle();

  if (error) {
    console.warn('[articles]', error.message);
    return { article: null, source: 'supabase', fetchError: error.message };
  }

  if (data) {
    return { article: rowToArticle(data), source: 'supabase' };
  }

  return { article: null, source: 'supabase' };
}

export async function fetchRelatedArticles(slug, practiceArea, mainSource) {
  if (mainSource !== 'supabase' || !isSupabaseConfigured() || !supabase) {
    return [];
  }

  const { data, error } = await supabase
    .from('articles')
    .select(
      'slug,title,title_vn,published_on,pa_slug,author_text,excerpt,excerpt_vn,has_pdf,pdf_note',
    )
    .eq('published', true)
    .eq('pa_slug', practiceArea)
    .neq('slug', slug)
    .order('published_on', { ascending: false })
    .limit(3);

  if (error) {
    console.warn('[articles related]', error.message);
    return [];
  }

  return (data ?? []).map((r) => rowToArticle(r));
}

export async function fetchTeamForAuthorPicker() {
  if (!isSupabaseConfigured() || !supabase) {
    return { members: [], error: 'not_configured' };
  }
  const { data, error } = await supabase
    .from('team_members')
    .select('slug,name,tier,sort_order')
    .eq('published', true)
    .order('tier', { ascending: true })
    .order('sort_order', { ascending: true });

  if (error) return { members: [], error: error.message };
  return { members: data ?? [], error: null };
}

export async function fetchTeamForAuthorPickerAdmin() {
  if (!isSupabaseConfigured() || !supabase) {
    return { members: [], error: 'not_configured' };
  }
  const { data, error } = await supabase
    .from('team_members')
    .select('slug,name,tier,sort_order,published')
    .order('tier', { ascending: true })
    .order('sort_order', { ascending: true });

  if (error) return { members: [], error: error.message };
  return { members: data ?? [], error: null };
}

export async function fetchArticlesForAdmin() {
  if (!supabase) return { rows: null, error: 'not_configured' };
  const { data, error } = await supabase
    .from('articles')
    .select(
      'id,slug,title,published_on,pa_slug,published,updated_by_email,updated_at',
    )
    .order('published_on', { ascending: false });
  if (error) return { rows: null, error: error.message };
  return { rows: data ?? [], error: null };
}

export async function fetchArticleByIdForAdmin(id) {
  if (!supabase) return { row: null, error: 'not_configured' };
  const { data, error } = await supabase.from('articles').select('*').eq('id', id).maybeSingle();
  if (error) return { row: null, error: error.message };
  return { row: data, error: null };
}

export async function insertArticle(payload) {
  if (!supabase) return { error: 'not_configured' };
  const { error } = await supabase.from('articles').insert(payload);
  return { error: error?.message ?? null };
}

export async function updateArticle(id, updates) {
  if (!supabase) return { error: 'not_configured' };
  const { error } = await supabase.from('articles').update(updates).eq('id', id);
  return { error: error?.message ?? null };
}

export async function deleteArticle(id) {
  if (!supabase) return { error: 'not_configured' };
  const { error } = await supabase.from('articles').delete().eq('id', id);
  return { error: error?.message ?? null };
}
